import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { STUDENT_NAV } from "@/lib/studentNav";
import { DashboardShell } from "@/components/DashboardShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, Images, FileUp, X, FileText, Loader2, Upload, Brain } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";

const NAV = STUDENT_NAV;

function readAsDataURL(file) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = src;
  });
}

async function buildPdf(dataUrls) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  for (let i = 0; i < dataUrls.length; i++) {
    if (i > 0) pdf.addPage();
    const img = await loadImage(dataUrls[i]);
    const ratio = Math.min(pageW / img.width, pageH / img.height);
    const w = img.width * ratio;
    const h = img.height * ratio;
    const fmt = dataUrls[i].includes("image/png") ? "PNG" : "JPEG";
    pdf.addImage(dataUrls[i], fmt, (pageW - w) / 2, (pageH - h) / 2, w, h);
  }
  return pdf.output("blob");
}

export default function CaptureUpload() {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [pages, setPages] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [stage, setStage] = useState("capture"); // capture | uploading | queueing
  const uploadedScript = useRef(null);
  const submitting = useRef(false);
  const cameraRef = useRef();
  const galleryRef = useRef();
  const pdfRef = useRef();

  useEffect(() => {
    api.get(`/question-papers/${paperId}`).then((r) => setPaper(r.data)).catch(() => toast.error("Paper not found"));
  }, [paperId]);

  const onImages = async (e) => {
    uploadedScript.current = null;
    const files = Array.from(e.target.files || []);
    const urls = await Promise.all(files.map(readAsDataURL));
    setPages((prev) => [...prev, ...urls]);
    setPdfFile(null);
    e.target.value = "";
  };

  const onPdf = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type !== "application/pdf") { toast.error("Please choose a PDF file"); return; }
    if (f) { uploadedScript.current = null; setPdfFile(f); setPages([]); }
    e.target.value = "";
  };

  const removePage = (i) => {
    uploadedScript.current = null;
    setPages((prev) => prev.filter((_, idx) => idx !== i));
  };

  const doUpload = async (blob, pageCount, filename) => {
    setStage("uploading");
    const fd = new FormData();
    fd.append("paper_id", paperId);
    fd.append("page_count", String(pageCount));
    fd.append("file", new File([blob], filename, { type: "application/pdf" }));
    if (!uploadedScript.current) {
      const { data } = await api.post("/answer-scripts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      uploadedScript.current = data;
    }
    const script = uploadedScript.current;
    setStage("queueing");
    await api.post(`/answer-scripts/${script.id}/evaluate`);
    toast.success("Submitted for evaluation. You can leave this page; we will alert you when your result is ready.");
    navigate("/student");
  };

  const submitImages = async () => {
    if (pages.length === 0) return toast.error("Capture at least one page");
    if (submitting.current) return;
    submitting.current = true;
    try {
      setStage("uploading");
      const blob = await buildPdf(pages);
      await doUpload(blob, pages.length, "answer_script.pdf");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setStage("capture");
    } finally {
      submitting.current = false;
    }
  };

  const submitPdf = async () => {
    if (!pdfFile || submitting.current) return;
    submitting.current = true;
    try {
      await doUpload(pdfFile, 1, pdfFile.name);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setStage("capture");
    } finally {
      submitting.current = false;
    }
  };

  return (
    <DashboardShell nav={NAV} title="Submit Answer Script">
      <div className="max-w-2xl mx-auto">
        {paper && (
          <div className="bg-background border border-border rounded-lg p-5 mb-6">
            <p className="overline text-muted-foreground">Exam</p>
            <h2 className="font-heading font-bold text-xl mt-1">{paper.title}</h2>
            <p className="text-sm text-muted-foreground">{paper.subject} · {paper.language} · {paper.total_marks} marks</p>
          </div>
        )}

        {/* hidden inputs */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={onImages} data-testid="camera-input" />
        <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={onImages} data-testid="gallery-input" />
        <input ref={pdfRef} type="file" accept="application/pdf" hidden onChange={onPdf} data-testid="pdf-input" />

        {stage === "capture" && (
          <>
            {pages.length === 0 && !pdfFile && (
              <div className="grid sm:grid-cols-3 gap-3">
                <button onClick={() => cameraRef.current?.click()} data-testid="take-photo-button" className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 hover:border-primary hover:bg-secondary/50 transition-colors">
                  <Camera className="h-8 w-8 text-primary" />
                  <span className="font-medium text-sm">Scan Answer Script</span>
                  <span className="text-xs text-muted-foreground text-center">Use your camera</span>
                </button>
                <button onClick={() => galleryRef.current?.click()} data-testid="choose-photos-button" className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 hover:border-primary hover:bg-secondary/50 transition-colors">
                  <Images className="h-8 w-8 text-primary" />
                  <span className="font-medium text-sm">Choose Answer Script</span>
                  <span className="text-xs text-muted-foreground text-center">From your gallery</span>
                </button>
                <button onClick={() => pdfRef.current?.click()} data-testid="upload-pdf-button" className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 hover:border-primary hover:bg-secondary/50 transition-colors">
                  <FileUp className="h-8 w-8 text-primary" />
                  <span className="font-medium text-sm">Upload Answer Script PDF</span>
                  <span className="text-xs text-muted-foreground text-center">Already have a PDF</span>
                </button>
              </div>
            )}

            {pdfFile && (
              <div className="bg-background border border-border rounded-lg p-6" data-testid="pdf-selected">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-primary" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{pdfFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(pdfFile.size / 1024).toFixed(0)} KB · PDF</p>
                  </div>
                  <button onClick={() => setPdfFile(null)} className="text-destructive"><X className="h-5 w-5" /></button>
                </div>
                <Button className="w-full rounded-full gap-2 mt-4" onClick={submitPdf} data-testid="submit-pdf-button">
                  <Upload className="h-4 w-4" /> Upload PDF & Evaluate
                </Button>
              </div>
            )}

            {pages.length > 0 && (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {pages.map((src, i) => (
                    <div key={i} className="relative group border border-border rounded-md overflow-hidden">
                      <img src={src} alt={`page ${i + 1}`} className="w-full h-28 object-cover" />
                      <span className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 rounded">{i + 1}</span>
                      <button onClick={() => removePage(i)} data-testid={`remove-page-${i}`} className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 mt-6">
                  <Button variant="outline" className="rounded-full gap-2" onClick={() => cameraRef.current?.click()} data-testid="add-camera-button">
                    <Camera className="h-4 w-4" /> Camera
                  </Button>
                  <Button variant="outline" className="rounded-full gap-2" onClick={() => galleryRef.current?.click()} data-testid="add-gallery-button">
                    <Images className="h-4 w-4" /> Gallery
                  </Button>
                  <Button className="rounded-full gap-2 flex-1" onClick={submitImages} data-testid="submit-script-button">
                    <Upload className="h-4 w-4" /> Convert to PDF & Evaluate ({pages.length} pages)
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {stage !== "capture" && (
          <div className="bg-background border border-border rounded-lg p-10 flex flex-col items-center gap-4 text-center" data-testid="processing-state">
            {stage === "uploading" ? <FileText className="h-10 w-10 text-primary" /> : <Brain className="h-10 w-10 text-primary" />}
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="font-heading font-bold text-lg">
              {stage === "uploading" ? "Uploading…" : "Starting background evaluation…"}
            </p>
            <p className="text-sm text-muted-foreground">Once your upload is submitted, evaluation continues in the background.</p>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
